import React from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';

/**
 * Support → «Τι θέλει προσοχή» (26/10/2026): ό,τι σπάει τώρα σε όλα τα καταστήματα του server, το σοβαρότερο πρώτο
 * (GET /api/support/attention — μόνο ανάγνωση). «Άνοιγμα» = η κάρτα του καταστήματος στην καρτέλα όπου διορθώνεται.
 * Τα δεδομένα τα φέρνει το SupportPanel (ανανέωση κάθε λεπτό, και ο αριθμός στην καρτέλα).
 */

export type AttentionKind = 'fiscal_unauthorized' | 'fiscal_stuck' | 'agent_offline' | 'print_stuck' | 'pending_accept'
  | 'fiscal_old' | 'platform_silent' | 'print_failed' | 'card_uncertain' | 'tickets_urgent' | 'billing_overdue' | 'doc_quota';
export interface AttentionItem {
  kind: AttentionKind;
  severity: 'critical' | 'warn';
  store_id: number;
  store_name: string;
  organization_name: string | null;
  count: number;
  since: string | null;
  detail: Record<string, any>;
}
export interface Attention {
  generated_at: string;
  summary: { critical: number; warn: number };
  items: AttentionItem[];
}

/** Σε ποια καρτέλα της κάρτας καταστήματος διορθώνεται κάθε σήμα. */
export const ATTENTION_TAB: Record<AttentionKind, 'fiscal' | 'overview' | 'printers' | 'orders' | 'platforms' | 'tickets' | 'billing'> = {
  fiscal_unauthorized: 'fiscal', fiscal_stuck: 'fiscal', fiscal_old: 'fiscal', agent_offline: 'overview', print_stuck: 'printers',
  print_failed: 'printers', pending_accept: 'orders', card_uncertain: 'orders', platform_silent: 'platforms', tickets_urgent: 'tickets',
  billing_overdue: 'billing', doc_quota: 'billing',
};
const CYCLE: Record<string, string> = {
  monthly: 'μηνιαίο', bimonthly: 'διμηνιαίο', quarterly: 'τριμηνιαίο', semiannual: 'εξαμηνιαίο', annual: 'ετήσιο',
};

const PLATFORM: Record<string, string> = { efood: 'efood', wolt: 'Wolt', box: 'BOX' };
const STATUS: Record<string, string> = {
  pending: 'σε αναμονή', sending: 'στέλνεται', transmission_failure: 'αποτυχία αποστολής', error: 'σφάλμα', validation_error: 'απόρριψη ελέγχου',
  unauthorized: 'απόρριψη token',
};
const byStatusText = (d: Record<string, any>) => Object.entries(d.by_status || {}).map(([s, n]) => `${n} ${STATUS[s] || s}`).join(', ');
const dateText = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Athens' }) : '');

/** «πριν από 12′» / «3 ώρες» / «2 ημέρες». */
export const agoText = (iso: string | null, now = Date.now()): string => {
  if (!iso) return '';
  const min = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (min < 60) return `πριν από ${min}′`;
  const h = Math.round(min / 60);
  if (h < 48) return `πριν από ${h} ${h === 1 ? 'ώρα' : 'ώρες'}`;
  return `πριν από ${Math.round(h / 24)} ημέρες`;
};

const eur = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;

/** Η πρόταση κάθε σήματος — τι συμβαίνει και τι σημαίνει. */
export const describeAttention = (i: AttentionItem, now = Date.now()): string => {
  const d = i.detail || {};
  switch (i.kind) {
    case 'fiscal_unauthorized':
      return `${i.count} παραστατικά: ο πάροχος (Oxygen) απορρίπτει τα στοιχεία — έλεγξε το token · ${agoText(i.since, now)}`;
    case 'fiscal_stuck':
      return `${i.count} παραστατικά δεν έχουν φτάσει στον πάροχο (${byStatusText(d)}) · το παλαιότερο ${agoText(i.since, now)}`;
    case 'fiscal_old':
      return `${i.count} παλιά παραστατικά (πάνω από 7 ημέρες) δεν στάλθηκαν ποτέ (${byStatusText(d)}) — για ξεκαθάρισμα · `
        + `το παλαιότερο από ${dateText(i.since)}`;
    case 'agent_offline':
      return `Ο print agent είναι εκτός σύνδεσης ενώ το κατάστημα δουλεύει (παραγγελία ${agoText(i.since, now)}) · ${d.printers} εκτυπωτές χωρίς PC`;
    case 'print_stuck':
      return `${i.count} εκτυπώσεις κολλημένες στην ουρά · η παλαιότερη ${agoText(i.since, now)}`;
    case 'pending_accept':
      return `${i.count} παραγγελίες ${(d.platforms || []).map((p: string) => PLATFORM[p] || p).join(' / ')} περιμένουν αποδοχή · ${agoText(i.since, now)}`;
    case 'platform_silent':
      return `${PLATFORM[d.platform] || d.platform}: καμία παραγγελία από ${agoText(i.since, now).replace('πριν από ', '')}, ενώ συνήθως ~${String(d.per_day).replace('.', ',')}/ημέρα — `
        + `και το κατάστημα έχει ${d.own_6h} δικές του παραγγελίες τις 6 τελευταίες ώρες`;
    case 'print_failed':
      return `${i.count} αποτυχημένες εκτυπώσεις τις τελευταίες 24 ώρες · η τελευταία ${agoText(i.since, now)}`;
    case 'card_uncertain':
      return `${i.count} πληρωμές κάρτας χωρίς βέβαιο αποτέλεσμα (24 ώρες, ${eur(Number(d.amount) || 0)}) — για έλεγχο · ${agoText(i.since, now)}`;
    case 'tickets_urgent':
      return `${i.count} ανοιχτά tickets υψηλής προτεραιότητας · το παλαιότερο ${agoText(i.since, now)}`;
    case 'billing_overdue':
      return `Πάγιο ${CYCLE[d.cycle] || ''} ${eur(Number(d.amount) || 0)}: ληξιπρόθεσμο ${i.count} ημέρες (από ${dateText(i.since)}) — `
        + 'έλεγξε αν πληρώθηκε και καταχώρισέ το';
    case 'doc_quota':
      return `Πακέτο παραστατικών του παρόχου στο ${d.pct}% (${i.count.toLocaleString('el-GR')} από ${Number(d.quota).toLocaleString('el-GR')})`
        + `${i.severity === 'critical' ? ' — ανανέωση πριν τελειώσει' : ''}`;
    default:
      return i.kind;
  }
};

const Row: React.FC<{ item: AttentionItem; onOpen: (storeId: number, tab: string) => void }> = ({ item, onOpen }) => {
  const critical = item.severity === 'critical';
  const Icon = critical ? AlertOctagon : AlertTriangle;
  return (
    <li className="px-4 py-3 flex items-start gap-3 bg-surface-2 dark:bg-surface-1" data-testid={`support-attention-${item.kind}-${item.store_id}`}>
      <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${critical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}
        aria-label={critical ? 'Κρίσιμο' : 'Προσοχή'} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-t1 truncate">
          {item.store_name} <span className="font-normal text-t4">#{item.store_id}{item.organization_name ? ` · ${item.organization_name}` : ''}</span>
        </p>
        <p className="text-xs text-t2">{describeAttention(item)}</p>
      </div>
      <button type="button" onClick={() => onOpen(item.store_id, ATTENTION_TAB[item.kind])}
        className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-line text-t2 hover:text-t1 hover:bg-surface-3">
        Άνοιγμα
      </button>
    </li>
  );
};

const SupportAttentionTab: React.FC<{
  data: Attention | null;
  failed: boolean;
  loading: boolean;
  onRefresh: () => void;
  onOpen: (storeId: number, tab: string) => void;
}> = ({ data, failed, loading, onRefresh, onOpen }) => {
  const critical = (data?.items || []).filter(i => i.severity === 'critical');
  const warn = (data?.items || []).filter(i => i.severity === 'warn');
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5" data-testid="support-attention">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-t1 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Τι θέλει προσοχή
          </h1>
          <p className="text-sm text-t3">
            Ό,τι σπάει τώρα σε όλα τα καταστήματα — το σοβαρότερο πρώτο.
            {data && ` Ενημερώθηκε ${new Date(data.generated_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}.`}
          </p>
        </div>
        <button onClick={onRefresh} disabled={loading} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg disabled:opacity-50" title="Ανανέωση">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!data && loading ? (
        <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
      ) : failed && !data ? (
        <p className="text-sm text-red-700 dark:text-red-300 text-center py-12">Η λίστα δεν φορτώθηκε.</p>
      ) : data && data.items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center" data-testid="support-attention-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
          <p className="text-sm font-medium text-t1">Όλα καλά</p>
          <p className="text-xs text-t3">Κανένα κατάστημα δεν θέλει κάτι αυτή τη στιγμή.</p>
        </div>
      ) : (
        <>
          {critical.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-red-700 dark:text-red-300">Κρίσιμα · {critical.length}</h2>
              <ul className="divide-y divide-line border border-red-500/30 rounded-xl overflow-hidden">
                {critical.map(i => <Row key={`${i.kind}-${i.store_id}`} item={i} onOpen={onOpen} />)}
              </ul>
            </section>
          )}
          {warn.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Για έλεγχο · {warn.length}</h2>
              <ul className="divide-y divide-line border border-line-strong rounded-xl overflow-hidden">
                {warn.map(i => <Row key={`${i.kind}-${i.store_id}`} item={i} onOpen={onOpen} />)}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default SupportAttentionTab;
